import './pagination.scss';

import React, { useState } from 'react';
import { difference, range } from 'lodash';

/**
 * Creates the sequence of numbers used to display the pagination based on the
 * total number of pages and the current page.  The sequence is made up of a
 * start, middle, and end ranges of numbers.  This implementation is a copy of
 * the pagination logic implemented on the documents page.
 *
 */
function createPaginationSequence(pageCount: number, currentPage: number):number[] {
  const pageNumbers = range(1, pageCount + 1);
  const startPages = pageNumbers.slice(0, 2);
  const endPages = pageNumbers.slice(Math.max(pageCount - 2, 0), pageCount);

  const middleRangeStart = Math.max(currentPage - 2, 0);
  const middleRangeEnd = middleRangeStart + 3;
  const middlePages = pageNumbers.slice(middleRangeStart, middleRangeEnd);

  return [
    ...startPages,
    ...difference(middlePages, startPages),
    ...difference(endPages, middlePages, startPages),
  ];
}
export interface PaginationProps {
  collection: {
    page?: number,
    pageCount?: number,
    count: number,
    itemsPerPage: number,
  }
  handlePageChange: Function,
}
const Pagination = (props: PaginationProps) => {
  const { collection, handlePageChange } = props;
  const [page, setPage] = useState(collection.page || 1);
  const setNewPage = (newPage: number):void => {
    setPage(newPage);
    handlePageChange(newPage);
  };
  const pageCount = collection.pageCount || Math.ceil(collection.count / collection.itemsPerPage);
  const paginationSequence = createPaginationSequence(pageCount, page);
  // early return when we don't want to show an empty paginator
  if (pageCount === 1) {
    return null;
  }

  return <ul className='rx-pagination'>
    <li className={`prev page${(page === 1) ? ' hide' : ''}`} key='prev'>
      <a className='rx-link paging-arrow prev' onClick={() => setNewPage(page - 1)}>
        &nbsp;
      </a>
    </li>
    {paginationSequence.map((val, index) => {
      if (index !== 0 && paginationSequence[index - 1] !== val - 1) {
        return <li className='ellipsis' key={`ell-${index}`}>...</li>;
      }
      return (<li className={'page'} key={val}>
        <a onClick={() => setNewPage(val)} className={`rx-link ${page === val ? ' selected' : ''}`}>
          { val }
        </a>
      </li>);
    })}
    <li className={`next page${(page < pageCount) ? '' : ' hide'}`} key='next'>
      <a className='rx-link paging-arrow next' onClick={() => setNewPage(page + 1)}>
        &nbsp;
      </a>
    </li>
  </ul>;
};


export default Pagination;
